import { useParams } from "react-router-dom";
import React, { useState, useEffect, useRef } from "react";
import axios from "../../lib/axios";
import GeneHuntViewer from "../../components/view/GeneHuntViewer";
import WorkflowTaskCard from "../../components/v2/task/workflow-task-card";

const WorkflowDetails = () => {
  const { workflow_id } = useParams();
  const [workflow, setWorkflow] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [progressData, setProgressData] = useState({
    progress: 0,
    progress_message: "",
    status: "",
  });
  const ws = useRef(null);

  // WebSocket connection
  useEffect(() => {
    // Only connect if workflow is not completed, workflow_id is available and WebSocket is not already connected
    if (workflow?.status?.toLowerCase() === "pending") {
      const wsUrl = `wss://1r9yo2hhm1.execute-api.us-west-2.amazonaws.com/production?id=${workflow_id}&type=workflow`;
      ws.current = new WebSocket(wsUrl);

      ws.current.onopen = () => {
        console.log("WebSocket Connected");
      };

      ws.current.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          console.log("WebSocket message received:", data);
          setProgressData({
            progress: data.progress || 0,
            progress_message: data.progress_message || "",
            status: data.status || "",
          });
        } catch (error) {
          console.error("Error parsing WebSocket message:", error);
        }
      };

      ws.current.onerror = (error) => {
        console.error("WebSocket error:", error);
      };

      ws.current.onclose = () => {
        console.log("WebSocket Disconnected");
      };
    }

    // Clean up WebSocket connection on component unmount
    return () => {
      if (ws.current) {
        ws.current.close();
      }
    };
  }, [workflow_id, workflow?.status]);

  useEffect(() => {
    const fetchTask = async (task_id) => {
      try {
        const response = await axios.get(`/v2/tasks/${task_id}`);
        const taskData = response.data.data;
        setTasks((prev) => [...prev, taskData]);
      } catch (err) {
        console.error(`Error fetching task ${task_id}:`, err);
      }
    };
    const fetchWorkflow = async () => {
      try {
        const response = await axios.get(`/v2/workflows/${workflow_id}`);
        setWorkflow(response.data.data);
        const taskPromises = response.data.data.tasks.map(async (task) => {
          return fetchTask(task);
        });
        await Promise.all(taskPromises);
      } catch (err) {
        setError("Workflow not found");
      } finally {
        setLoading(false);
      }
    };

    fetchWorkflow();
  }, [workflow_id]);

  if (loading) {
    return (
      <div className="w-full p-16 flex items-center justify-center">
        <div className="text-gray-500">Loading workflow details...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-full p-16 flex items-center justify-center">
        <div className="text-red-500">{error}</div>
      </div>
    );
  }

  if (!workflow) {
    return (
      <div className="w-full p-16 flex items-center justify-center">
        <div className="text-gray-500">Workflow not found</div>
      </div>
    );
  }

  return (
    <section className="w-full p-16">
      <div className="w-full flex flex-col items-center justify-center gap-7">
        {/* header */}
        <div className="w-full rounded shadow-sm p-4 border bg-white flex justify-between">
          <div>
            <h1 className="text-lg font-bold text-gray-500">
              WORKFLOW:{" "}
              <span className="text-blue-500"> {workflow.workflow_id}</span>
            </h1>
            {(workflow?.status?.toLowerCase() === "completed" ||
              progressData.status?.toLowerCase() === "completed") && (
              <a
                href={`https://storage.kinggenomics.com/output/workflow/${workflow.workflow_id}/result-combined.zip`}
                rel="noreferrer"
                className="text-blue-500 uppercase text-sm hover:underline cursor-pointer"
              >
                Download Combined Result
              </a>
            )}
          </div>
          {/* <button className="bg-blue-500 text-white px-4 py-2 rounded">
            Restart Workflow
          </button> */}
          <div className="flex flex-col">
            <p className="text-gray-500 uppercase text-sm">
              {progressData.status || workflow?.status?.toLowerCase()}
            </p>
            {(workflow?.status?.toLowerCase() === "completed" || progressData.status?.toLowerCase() === "completed") ? null : (
              <div className="flex items-center gap-3">
                <div className="flex-1 w-56">
                  <div className="w-full bg-gray-200 rounded-full h-3">
                    <div
                      className="bg-blue-600 h-3 rounded-full transition-all duration-300"
                      style={{ width: `${progressData.progress || 0}%` }}
                    ></div>
                  </div>
                  <p className="text-gray-500 text-xs mt-1">
                    {progressData.progress_message || workflow.progress_message}
                  </p>
                </div>
                <div className="animate-spin h-5 w-5 border-2 border-blue-500 border-t-transparent rounded-full"></div>
              </div>
            )}
          </div>
        </div>

        {/* tasks */}
        <div className="w-full gap-2 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4">
          {tasks?.map((task) => {
            return (
              <WorkflowTaskCard key={task?.task_id} task={task} />
            );
          })}
        </div>

        {(workflow?.status?.toLowerCase() === "completed" ||
          progressData.status?.toLowerCase() === "completed") && (
          <div className="w-full mt-5">
            <GeneHuntViewer type="workflow" id={workflow.workflow_id} />
          </div>
        )}
      </div>
    </section>
  );
};
export default WorkflowDetails;
